import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { RoleOption, VolumeOption, WizardStep } from '../schemas/wizard-schema';

interface OnboardingDraft {
  workspace: {
    name: string;
    slug: string;
    baseCurrency: 'USD' | 'USDT' | 'BRL' | 'EUR';
    timezone: string;
    tradingIntent?: string;
  } | null;
  role: {
    role: RoleOption;
    monthlyVolume: VolumeOption;
  } | null;
  consent: {
    riskAcknowledged: boolean;
    termsAccepted: boolean;
    paperFirstCommitted: boolean;
  } | null;
  /** Last step that was successfully completed (1–4); 0 = nothing done. */
  lastCompleted: number;
  setWorkspace: (v: OnboardingDraft['workspace']) => void;
  setRole: (v: OnboardingDraft['role']) => void;
  setConsent: (v: OnboardingDraft['consent']) => void;
  markStepDone: (step: WizardStep) => void;
  reset: () => void;
}

const initial = {
  workspace: null,
  role: null,
  consent: null,
  lastCompleted: 0,
};

const STEP_TO_INDEX: Record<WizardStep, number> = {
  welcome: 1,
  workspace: 2,
  role: 3,
  risk: 4,
};

export const useOnboardingDraftStore = create<OnboardingDraft>()(
  persist(
    (set, get) => ({
      ...initial,
      setWorkspace: (v) => set({ workspace: v }),
      setRole: (v) => set({ role: v }),
      setConsent: (v) => set({ consent: v }),
      markStepDone: (step) => {
        const idx = STEP_TO_INDEX[step];
        if (idx > get().lastCompleted) set({ lastCompleted: idx });
      },
      reset: () => set(initial),
    }),
    {
      name: 'loki:onboarding-draft',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        workspace: s.workspace,
        role: s.role,
        consent: s.consent,
        lastCompleted: s.lastCompleted,
      }),
    },
  ),
);
